/**
 * Session Serializer — HLOS-44 (Sprint 4).
 *
 * Converte o estado dos 4 bounded contexts (conversation, presence, context,
 * session) + companion orchestrator em um SessionSnapshot serializável,
 * e restaura esse snapshot para os stores correspondentes.
 *
 * Persistência: o SessionSnapshot vai para `vault.writeData()`, que re-cifra
 * com a DEK do vault (CL-08: zero plaintext em OPFS).
 *
 * Versionamento: campo `version` permite migration futura (HLOS-44 v2).
 *
 * Constitucional:
 * - CL-07 (Modelo = ativo): sessão persiste 100% local-first
 * - CL-08 (Ética fechada): snapshot cifrado com DEK do vault (mesma chave)
 * - CL-09 (Explicabilidade): log de restoreSession + persistSession
 * - SL-01..04 (Scientific Locks): modelo + estado do kernel não muda
 */

import type { Message, AttachmentMetadata } from "../conversation/types";
import type { Presence } from "../presence/types";
import type { CuriosityGap, Goal } from "../context/types";
import type { Thread } from "../session/types";
import { useConversationStore } from "../conversation/store";
import { usePresenceStore } from "../presence/store";
import { useContextStore } from "../context/store";
import { useSessionStore } from "../session/store";
import { useCompanionStore } from "../companion/store";

/** Versão atual do schema. Incrementar quando houver migration. */
export const SESSION_SNAPSHOT_VERSION = 1;

/**
 * HLOS-374: mensagem persistida no snapshot. NÃO carrega `dataUrl` dos
 * anexos (imagens base64 podem ter 4MB+ cada e multiplicam o vault por turno).
 * Apenas metadata leve é persistida para auditoria/explicabilidade.
 */
export interface PersistedMessage {
  readonly id: string;
  readonly role: "learner" | "companion";
  readonly text: string;
  readonly createdAt: number;
  readonly attachments?: readonly AttachmentMetadata[];
}

/** Shape do snapshot persistido. */
export interface SessionSnapshot {
  readonly version: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly conversation: {
    readonly messages: readonly PersistedMessage[];
  };
  readonly presence: {
    readonly state: Presence["state"];
    readonly lastSignal: Presence["lastSignal"];
    readonly updatedAt: number;
  };
  readonly context: {
    readonly activeGoal: Goal | null;
    readonly curiosityGaps: readonly CuriosityGap[];
  };
  readonly session: {
    readonly currentThread: Thread | null;
  };
  readonly companion: {
    readonly state: string;
    readonly lastTurnAt: number | null;
  };
}

/**
 * HLOS-374: strip o `dataUrl` (potencialmente 4MB+) e mantém apenas metadata
 * leve para auditoria. Constitucional: Princípio X (não finge ter o que não
 * tem — restore não vai re-anexar a imagem) + CL-09 (metadata preservada
 * permite explicar ao learner "que imagens você enviou nesta sessão").
 */
function stripAttachments(msg: Message): PersistedMessage {
  if (!msg.attachments || msg.attachments.length === 0) {
    return {
      id: msg.id,
      role: msg.role,
      text: msg.text,
      createdAt: msg.createdAt,
    };
  }
  const metadata: AttachmentMetadata[] = msg.attachments.map((a) => {
    // dataUrl é "data:<mime>;base64,<base64>" → base64 ≈ 3/4 do tamanho real.
    const commaIdx = a.dataUrl.indexOf(",");
    const base64Len = commaIdx >= 0 ? a.dataUrl.length - commaIdx - 1 : a.dataUrl.length;
    const sizeBytes = Math.floor((base64Len * 3) / 4);
    return {
      kind: a.kind,
      ...(a.filename ? { filename: a.filename } : {}),
      sizeBytes,
    };
  });
  return {
    id: msg.id,
    role: msg.role,
    text: msg.text,
    createdAt: msg.createdAt,
    attachments: metadata,
  };
}

/**
 * Captura snapshot atual dos 4 bounded contexts + companion.
 * Idempotente. Não lança (best-effort).
 */
export function captureSnapshot(): SessionSnapshot {
  const conversation = useConversationStore();
  const presence = usePresenceStore();
  const context = useContextStore();
  const session = useSessionStore();
  const companion = useCompanionStore();

  const now = Date.now();

  return {
    version: SESSION_SNAPSHOT_VERSION,
    createdAt: now,
    updatedAt: now,
    conversation: {
      // HLOS-374: strip dataUrl dos anexos antes de persistir.
      messages: conversation.messages.map(stripAttachments),
    },
    presence: {
      state: presence.state,
      lastSignal: presence.lastSignal,
      updatedAt: presence.updatedAt,
    },
    context: {
      activeGoal: context.activeGoal,
      curiosityGaps: [...context.curiosityGaps],
    },
    session: {
      currentThread: session.currentThread ? { ...session.currentThread } : null,
    },
    companion: {
      state: companion.state,
      lastTurnAt: companion.lastTurnAt,
    },
  };
}

/**
 * Restaura snapshot nos 4 bounded contexts + companion.
 * Idempotente. Em caso de erro, log e segue (estado vazio é aceitável).
 *
 * @returns true se restaurou; false se snapshot incompatível / corrompido.
 */
export function restoreSnapshot(snapshot: SessionSnapshot): boolean {
  // Validação básica.
  if (!snapshot || typeof snapshot !== "object") return false;
  if (snapshot.version !== SESSION_SNAPSHOT_VERSION) {
    console.warn(
      `[session] snapshot version ${snapshot.version} != ${SESSION_SNAPSHOT_VERSION}; ignorando`,
    );
    return false;
  }
  if (!snapshot.conversation || !Array.isArray(snapshot.conversation.messages)) {
    return false;
  }

  const conversation = useConversationStore();
  const presence = usePresenceStore();
  const context = useContextStore();
  const session = useSessionStore();

  // Conversation: substituir messages (drop-in). Note: messages são readonly no store;
  // usamos um trick: clear() + append() cada uma. Mais simples: reatribuir via store.
  conversation.clear();
  for (const msg of snapshot.conversation.messages) {
    conversation.append(msg.role, msg.text);
    // HLOS-374: anexos do snapshot não carregam dataUrl (metadata-only).
    // Restore é text-only — imagem some após boot. Constitucionalmente OK
    // (Princípio X: dignidade, não finge ter o que não tem). Log dev-only
    // para auditoria (CL-09).
    if (msg.attachments && msg.attachments.length > 0) {
      const totalBytes = msg.attachments.reduce(
        (sum: number, a: AttachmentMetadata) => sum + a.sizeBytes,
        0,
      );
      console.info(
        `[HLOS-374] restore: ${msg.attachments.length} anexo(s) omitido(s) ` +
          `(metadata-only, ${(totalBytes / 1024).toFixed(1)}KB). ` +
          `Re-anexo manual pelo aprendiz.`,
      );
    }
  }

  // Presence: reaplicar sinal.
  presence.applySignal(snapshot.presence.lastSignal, snapshot.presence.state);

  // Context: restaurar goal + curiosity gaps.
  context.setGoal(snapshot.context.activeGoal);
  context.clearCuriosityGaps();
  for (const gap of snapshot.context.curiosityGaps) {
    context.addCuriosityGap(gap);
  }

  // Session: restaurar thread (se existir).
  if (snapshot.session.currentThread) {
    session.openThread(snapshot.session.currentThread);
  }

  // Companion: restaurar lastTurnAt apenas (state é volátil, sempre começa "idle").
  // Não restauramos `state` (companion.state) — sempre começa em "idle" no boot.

  return true;
}

/**
 * Valida estrutura mínima do snapshot.
 */
export function isValidSnapshot(value: unknown): value is SessionSnapshot {
  if (!value || typeof value !== "object") return false;
  const s = value as Record<string, unknown>;
  return (
    s.version === SESSION_SNAPSHOT_VERSION &&
    Array.isArray((s.conversation as Record<string, unknown>)?.messages) &&
    typeof (s.presence as Record<string, unknown>)?.state === "string"
  );
}
